/* The Optimizer's data on this station: what the scheduled check proposed, what the
 * desk has dismissed and ticked, and what became of each line approved.
 *
 * A salon without the licence makes no call for it. A salon licensed with it switched off
 * makes one read of its settings and stops: no entry, no channel, no poll. A station left
 * open finds out it was switched on at its next reload or sign-in. A read that fails keeps
 * what is on screen, for the reason js/dreamcloud.js does, and tries again later.
 *
 * Nothing here interrupts anyone. No modal, sound or toast, on any outcome: the desk
 * comes to this between turns, and a count on the switcher is all it ever says by itself.
 *
 * The rules and wording are js/rules/optimizer-view.js. Dry run is not here: the
 * function reads it from the settings row and ignores anything sent.
 */
import * as Vue from 'vue';
import { licensing } from './licensing.js';
import { optimizerApi } from './optimizer-api.js';
import { dismissalKeys } from './rules/optimizer.js';
import {
  approveBatches, checkNowMessage, dayGroups, handEntry, handKey, pendingCount, pruneHand, runBatches, salonToday,
  ticksFor as ticksOver
} from './rules/optimizer-view.js';
import { sync } from './sync.js';

// Realtime carries the changes. This is for when it doesn't.
var POLL_MS = 120000;
var REFETCH_MS = 800;
// How often the salon's date is worked out again.
var CLOCK_MS = 60000;

var state = Vue.reactive({
  settings: null,
  blocks: [],
  days: [],
  dismissals: [],
  // handKey -> handEntry, only what the desk has changed by hand. See ticksFor in the rules.
  hand: {},
  // Line id -> what the function said.
  results: {},
  // Block key -> the sentence for a call with no lines to put it on, and whether it's going.
  messages: {},
  busy: {},
  checking: false,
  checkMessage: null,
  // Which days the desk has opened or closed, by date.
  open: {},
  timeZone: null,
  today: salonToday(new Date(), null)
});

// Dismissed here with the write still on its way, so a read cannot bring them back.
var dismissedHere = [];

var channel = null;
var pollTimer = null;
var clockTimer = null;
var refetchTimer = null;
var retryTimer = null;
// Goes up when the sign-in or salon changes, so a read still on its way lands nowhere.
var generation = 0;

var enabled = Vue.computed(function () {
  return !!state.settings && state.settings.enabled === true;
});

// Only a literal false is off, the same as the function. Not knowing is a dry run.
var dryRun = Vue.computed(function () {
  return !(state.settings && state.settings.dry_run === false);
});

// Whether the module is on the list. Nothing until the settings have said so.
var offered = Vue.computed(function () {
  return licensing.licensed('optimizer') && enabled.value;
});

function viewOpts() {
  return { days: state.days, dismissals: state.dismissals };
}

var groups = Vue.computed(function () {
  return enabled.value ? dayGroups(state.blocks, state.today, viewOpts()) : [];
});

var count = Vue.computed(function () {
  return enabled.value ? pendingCount(state.blocks, state.today, viewOpts()) : 0;
});

function shouldRead() {
  return !!sync.auth.user && licensing.licensed('optimizer');
}

async function rows(query) {
  var res = await query;
  if (res.error) throw new Error(res.error.message);
  return res.data || [];
}

function clearDay() {
  state.blocks = [];
  state.days = [];
  state.dismissals = [];
}

async function load() {
  if (!shouldRead()) return;
  var mine = generation;
  try {
    var settings = await rows(sync.sb.from('tt_optimizer_settings')
      .select('enabled, dry_run, last_check_ok_at, last_check_error').limit(1));
    if (mine !== generation) return;
    state.settings = settings[0] || null;
    if (!enabled.value) {
      stopLive();
      clearDay();
      return;
    }
    var config = await sync.sb.from('tt_mangomint_api_config').select('features').maybeSingle();
    if (mine !== generation) return;
    if (!config.error && config.data && config.data.features) state.timeZone = config.data.features.timezone || null;
    state.today = salonToday(new Date(), state.timeZone);
    var today = state.today;
    var read = await Promise.all([
      rows(sync.sb.from('tt_optimizer_blocks').select('date, stable_key, block').gte('date', today)),
      rows(sync.sb.from('tt_optimizer_days').select('date, highlight').gte('date', today)),
      rows(sync.sb.from('tt_optimizer_dismissals').select('date, booking_key').gte('date', today))
    ]);
    if (mine !== generation) return;
    state.blocks = read[0].map(function (r) {
      return Object.assign({}, r.block, { date: r.date, key: r.stable_key });
    });
    state.days = read[1];
    dismissedHere = dismissedHere.filter(function (d) { return d.date >= today; });
    state.dismissals = read[2].concat(dismissedHere.filter(function (d) {
      return !read[2].some(function (r) { return r.date === d.date && r.booking_key === d.booking_key; });
    }));
    pruneResults();
    state.hand = pruneHand(state.hand, state.blocks);
    startLive();
  } catch (e) {
    // Not knowing isn't being off. Nothing else is running to try again if it never got going.
    if (mine === generation && !channel && !retryTimer) {
      retryTimer = setTimeout(function () { retryTimer = null; load(); }, POLL_MS);
    }
  }
}

// A line a check has since dropped has nothing left to report on.
function pruneResults() {
  var live = {};
  state.blocks.forEach(function (b) { (b.lines || []).forEach(function (l) { live[l.id] = true; }); });
  Object.keys(state.results).forEach(function (id) {
    if (!live[id]) delete state.results[id];
  });
}

/* ---- Ticking ---- */

function ticksFor(block) {
  return ticksOver(block, state.hand);
}

function setTick(block, line, on) {
  state.hand[handKey(block.date, line.id)] = handEntry(line, on);
}

/* ---- Approve ---- */

async function approve(block, ids) {
  var key = block.key;
  if (state.busy[key] || !ids.length) return;
  state.busy[key] = true;
  state.messages[key] = null;
  ids.forEach(function (id) { delete state.results[id]; });
  try {
    var out = await runBatches(approveBatches(block, ids), optimizerApi.approveMoves);
    Object.keys(out.results).forEach(function (id) { state.results[id] = out.results[id]; });
    state.messages[key] = out.message;
    await load();
  } finally {
    state.busy[key] = false;
  }
}

/* ---- Dismiss ---- */

/* Through the queue, as a fact that happened at the desk: it goes up when the wifi comes
 * back. `ignoreDuplicates` because a station may add these and never update them, and
 * two stations can dismiss one booking. */
function dismiss(date, blockOrLine) {
  dismissalKeys(blockOrLine).forEach(function (key) {
    var row = { date: date, booking_key: key };
    if (state.dismissals.some(function (d) { return d.date === date && d.booking_key === key; })) return;
    dismissedHere.push(row);
    state.dismissals.push(row);
    sync.enqueue({
      table: 'tt_optimizer_dismissals',
      type: 'upsert',
      row: Object.assign({}, row),
      onConflict: 'tenant_id,date,booking_key',
      ignoreDuplicates: true
    });
  });
}

/* ---- Check now ---- */

async function checkNow() {
  if (state.checking) return;
  state.checking = true;
  state.checkMessage = null;
  try {
    state.checkMessage = checkNowMessage(await optimizerApi.checkNow());
    await load();
  } finally {
    state.checking = false;
  }
}

/* ---- Keeping up ---- */

function scheduleLoad() {
  if (refetchTimer) clearTimeout(refetchTimer);
  refetchTimer = setTimeout(function () { refetchTimer = null; load(); }, REFETCH_MS);
}

/* The date only reads again when it changes, and the channel doesn't care what day it is. */
function tick() {
  var now = salonToday(new Date(), state.timeZone);
  if (now !== state.today) {
    state.today = now;
    load();
  }
}

function startLive() {
  if (channel) return;
  channel = sync.subscribeOptimizer(scheduleLoad);
  pollTimer = setInterval(load, POLL_MS);
  clockTimer = setInterval(tick, CLOCK_MS);
}

function stopLive() {
  if (channel) { sync.removeChannel(channel); channel = null; }
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (clockTimer) { clearInterval(clockTimer); clockTimer = null; }
  if (refetchTimer) { clearTimeout(refetchTimer); refetchTimer = null; }
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
}

Vue.watch(function () {
  return [shouldRead(), sync.auth.tenantId].join('|');
}, function () {
  stopLive();
  dismissedHere = [];
  state.settings = null;
  generation += 1;
  state.timeZone = null;
  state.hand = {};
  clearDay();
  if (shouldRead()) load();
}, { immediate: true });

export const optimizer = {
  state: state,
  enabled: enabled,
  dryRun: dryRun,
  groups: groups,
  count: count,
  offered: offered,
  load: load,
  ticksFor: ticksFor,
  setTick: setTick,
  approve: approve,
  dismiss: dismiss,
  checkNow: checkNow
};
